// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  // Estágios na ordem: ATRASO AO CLIENTE · RISCO OCULTO · EM ANDAMENTO · ANTECIPADO TOTAL
  stages: ["#D04638", "#E08A1E", "#2A75B0", "#2C8D5E"],
  stagesSoft: ["#FBE8E5", "#FBEFD9", "#E2EDF7", "#DEEEE5"],

  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  neutral: "#6B6B6B",
  neutralSoft: "#ECEAE2",
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes tracking-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(44, 141, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(44, 141, 94, 0); }
  }

  .tracking-uf-row:hover { background: #EFE9DA; }
  .tracking-modal-close:hover { background: #E5E1D5 !important; color: #1A1A1A !important; }
  .tracking-stage { transition: box-shadow 0.15s ease, transform 0.15s ease; }
  .tracking-stage:hover { box-shadow: 0 6px 16px rgba(0,0,0,0.10); transform: translateY(-2px); }
  .tracking-uf-path { transition: fill-opacity 0.12s ease, stroke 0.12s ease; }
  .tracking-uf-path:hover { fill-opacity: 0.82; stroke: #1A1A1A; stroke-width: 1.4; }
  .tracking-export-btn:hover:not(:disabled) { background: #CDE8D8 !important; }
  .tracking-export-btn:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 20px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1920,
  margin: "0 auto",
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 2,
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    margin: 0,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  pill: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 16px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    cursor: active ? "default" : "pointer",
    userSelect: "none",
  }),
  pillDot: (loading) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: loading ? colors.stages[1] : colors.stages[3],
    animation: loading ? "tracking-pulse 1.2s infinite" : "tracking-pulse 1.8s infinite",
  }),
  clock: {
    fontFamily: fonts.mono,
    fontSize: 30,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
    color: colors.text,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Filtro de período (data + hora)
// ─────────────────────────────────────────────────────────────────────────────
export const filterBar = {
  wrap: (active) => ({
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 12,
    background: colors.surface,
    // Filtro ligado muda o significado de TODOS os números da tela, então a
    // barra deixa de ser neutra e ganha a faixa colorida.
    border: `1px solid ${active ? colors.stages[2] : colors.border}`,
    borderLeft: `4px solid ${active ? colors.stages[2] : colors.border}`,
    borderRadius: 14,
    padding: "14px 18px",
  }),
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: colors.textSoft,
  },
  input: {
    background: colors.bg,
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 9,
    padding: "9px 12px",
    fontSize: 14,
    fontFamily: fonts.mono,
    color: colors.text,
    outline: "none",
    minWidth: 0,
  },
  actions: {
    display: "flex",
    gap: 8,
    marginLeft: "auto",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  btn: (primary) => ({
    border: primary ? "none" : `1px solid ${colors.borderStrong}`,
    background: primary ? colors.stages[2] : colors.surface,
    color: primary ? "#FFFFFF" : colors.textMuted,
    borderRadius: 9,
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.02em",
    cursor: "pointer",
    fontFamily: fonts.body,
    whiteSpace: "nowrap",
  }),
  note: (active) => ({
    width: "100%",
    fontSize: 12.5,
    color: active ? colors.stages[2] : colors.textSoft,
    fontWeight: active ? 600 : 400,
    letterSpacing: "0.01em",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Distribuição (status final dos pedidos · dual SLA)
// ─────────────────────────────────────────────────────────────────────────────
export const distribution = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 28px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 18,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  totalBlock: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    padding: "8px 16px",
    background: colors.bg,
    borderRadius: 10,
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  totalValue: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    fontSize: 20,
    color: colors.text,
    letterSpacing: "-0.01em",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 12,
    marginBottom: 14,
  },
  stage: (color, clickable) => ({
    padding: "16px 18px",
    borderRadius: 12,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    position: "relative",
    overflow: "hidden",
    borderLeft: `4px solid ${color}`,
    cursor: clickable ? "pointer" : "default",
  }),
  stageLabel: (color) => ({
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    marginBottom: 8,
    color,
  }),
  stageValue: {
    fontSize: 32,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.025em",
    lineHeight: 1,
    color: colors.text,
  },
  stageUnit: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 5,
  },
  stagePct: {
    fontSize: 12.5,
    color: colors.textMuted,
    marginTop: 6,
    fontWeight: 500,
  },
  bar: {
    display: "flex",
    height: 16,
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${colors.border}`,
    background: colors.bg,
  },
  barSeg: (color, width) => ({
    background: color,
    width: `${width}%`,
    transition: "width 0.6s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Cards (4 visões: cliente, contratual, lead time, comercial)
// ─────────────────────────────────────────────────────────────────────────────
export const cards = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
  },
  card: {
    background: colors.surface,
    borderRadius: 18,
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  accent: (i) => ({
    height: 6,
    width: "100%",
    background: colors.stages[i],
  }),
  head: {
    padding: "20px 22px 18px",
    borderBottom: `1px solid ${colors.border}`,
  },
  stageTag: {
    display: "inline-block",
    fontSize: 12,
    letterSpacing: "0.12em",
    color: colors.textSoft,
    fontWeight: 600,
    marginBottom: 6,
  },
  title: (i) => ({
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.015em",
    marginBottom: 10,
    lineHeight: 1,
    color: colors.stages[i],
    margin: 0,
  }),
  count: {
    fontSize: 15,
    color: colors.textMuted,
    fontVariantNumeric: "tabular-nums",
  },
  countStrong: {
    fontWeight: 700,
    color: colors.text,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Metrics 2x2 dentro de cada card
// ─────────────────────────────────────────────────────────────────────────────
export const metrics = {
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    flex: 1,
  },
  cell: (idx) => {
    const isLeftColumn = idx % 2 === 0;
    const isTopRow = idx < 2;
    return {
      padding: "18px 18px 20px",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      borderRight: isLeftColumn ? `1px solid ${colors.border}` : "none",
      borderBottom: isTopRow ? `1px solid ${colors.border}` : "none",
    };
  },
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: colors.text,
    lineHeight: 1.35,
    marginBottom: 14,
    minHeight: 32,
  },
  value: (state) => {
    const map = {
      below: colors.good,
      above: colors.bad,
      equal: colors.neutral,
    };
    return {
      fontSize: 34,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.025em",
      lineHeight: 1,
      color: state ? map[state] : colors.text,
    };
  },
  unit: {
    fontSize: 15,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 3,
  },
  compare: (state) => {
    const map = {
      below: { bg: colors.goodSoft, color: colors.good },
      above: { bg: colors.badSoft, color: colors.bad },
      equal: { bg: colors.neutralSoft, color: colors.neutral },
    };
    const c = map[state] || map.equal;
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      marginTop: 8,
      fontSize: 12,
      fontWeight: 600,
      padding: "4px 8px",
      borderRadius: 5,
      letterSpacing: "0.01em",
      alignSelf: "flex-start",
      background: c.bg,
      color: c.color,
    };
  },
  compareArrow: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Cartograma do Brasil (grade de UFs) · Entregas × Atrasos
// ─────────────────────────────────────────────────────────────────────────────
export const brmap = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "20px 24px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 18,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  toggle: {
    display: "inline-flex",
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: 3,
    gap: 2,
  },
  toggleBtn: (active, color) => ({
    border: "none",
    cursor: active ? "default" : "pointer",
    borderRadius: 999,
    padding: "7px 18px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    fontFamily: "inherit",
    background: active ? colors.surface : "transparent",
    color: active ? color : colors.textMuted,
    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  totalBlock: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    padding: "6px 14px",
    background: colors.bg,
    borderRadius: 10,
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  totalValue: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    fontSize: 18,
    color: colors.text,
    letterSpacing: "-0.01em",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 18,
  },
  mapBox: {
    width: "100%",
    maxWidth: 760,
    display: "flex",
    justifyContent: "center",
  },
  svg: {
    width: "100%",
    height: "auto",
    display: "block",
    overflow: "visible",
  },
  path: (bg, clickable) => ({
    fill: bg,
    stroke: colors.surface,
    strokeWidth: 1,
    strokeLinejoin: "round",
    cursor: clickable ? "pointer" : "default",
  }),
  label: {
    pointerEvents: "none",
    textAnchor: "middle",
    fontFamily: fonts.body,
    stroke: "#FFFFFF",
    strokeWidth: 2.2,
    strokeLinejoin: "round",
    paintOrder: "stroke",
  },
  labelUf: {
    fontSize: 12.5,
    fontWeight: 800,
    fill: colors.text,
  },
  labelEnt: {
    fontSize: 11,
    fontWeight: 700,
    fill: "#176B3E",
  },
  labelAtr: {
    fontSize: 11,
    fontWeight: 700,
    fill: "#9E2B1E",
  },
  leader: {
    stroke: colors.textSoft,
    strokeWidth: 1,
    strokeDasharray: "4 3",
    fill: "none",
    pointerEvents: "none",
  },
  leaderDot: {
    fill: colors.textMuted,
    pointerEvents: "none",
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  legendLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
    fontVariantNumeric: "tabular-nums",
  },
  legendBar: (light, dark) => ({
    width: 180,
    height: 12,
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: `linear-gradient(90deg, ${light}, ${dark})`,
  }),
  legendSep: {
    width: 1,
    height: 16,
    background: colors.borderStrong,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
  },
  legendDot: (bg) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    background: bg,
    border: `1px solid ${colors.border}`,
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────
export const footer = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 6px 0",
    fontSize: 13,
    color: colors.textMuted,
    flexWrap: "wrap",
    gap: 12,
  },
  legend: {
    display: "flex",
    gap: 22,
    flexWrap: "wrap",
    alignItems: "center",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: colors.text,
    fontSize: 13,
    letterSpacing: "0.04em",
  },
  legendCompare: {
    fontWeight: 500,
    color: colors.textMuted,
    letterSpacing: 0,
  },
  swatch: (bg) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    background: bg,
  }),
  divider: {
    width: 1,
    height: 16,
    background: colors.borderStrong,
  },
  schedule: {
    fontSize: 13,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Painel "ENTREGAS" · donut + breakdown
// ─────────────────────────────────────────────────────────────────────────────
export const deliveryColors = {
  sucesso: colors.stages[3], // verde
  transito: "#5BC4B8",        // teal (estilo da imagem 2)
  ocorrencia: colors.stages[1], // laranja
  problema: colors.stages[0],   // vermelho
};

export const deliveryRow = {
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 2fr",
    gap: 16,
  },
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  accent: (bg) => ({
    height: 6,
    width: "100%",
    background: bg,
  }),
  head: {
    padding: "18px 22px 14px",
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
};

export const donut = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 22,
    padding: "24px 22px 26px",
    flex: 1,
  },
  chartBox: {
    position: "relative",
    width: 200,
    height: 200,
    flexShrink: 0,
  },
  hole: {
    position: "absolute",
    inset: 36,
    background: colors.surface,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  holeValue: {
    fontSize: 42,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.025em",
    color: colors.text,
    lineHeight: 1,
  },
  holeLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: colors.textMuted,
    marginTop: 4,
  },
  breakdownGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 20,
    rowGap: 8,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: colors.textMuted,
    marginBottom: 2,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  rowLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 500,
    color: colors.text,
  },
  rowDot: (bg) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: bg,
  }),
  rowValue: (color) => ({
    fontFamily: fonts.mono,
    fontSize: 16,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: color || colors.text,
    letterSpacing: "-0.01em",
  }),
};

export const performance = {
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "8px 0",
    flex: 1,
  },
  emoji: {
    fontSize: 46,
    lineHeight: 1,
  },
  value: (good) => ({
    fontSize: 56,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1,
    color: good ? colors.good : colors.bad,
    fontVariantNumeric: "tabular-nums",
  }),
  counts: {
    marginTop: 12,
    display: "flex",
    gap: 18,
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    fontVariantNumeric: "tabular-nums",
  },
  countItem: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 6,
  },
  countNumber: (color) => ({
    color,
    fontFamily: fonts.mono,
    fontSize: 16,
    fontWeight: 700,
  }),
};

export const carriers = {
  body: {
    padding: "10px 22px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 380,
    overflowY: "auto",
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    padding: "12px 22px 0",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
  },
  legendDot: (bg) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: bg,
    flexShrink: 0,
  }),
  meta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: "0 22px",
    marginTop: 14,
    marginBottom: 8,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: colors.textMuted,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(160px, 240px) 1fr 48px",
    alignItems: "center",
    gap: 14,
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barWrap: {
    display: "flex",
    height: 14,
    borderRadius: 6,
    overflow: "hidden",
    background: colors.bg,
    border: `1px solid ${colors.border}`,
  },
  barSeg: (bg, width) => ({
    background: bg,
    width: `${width}%`,
    transition: "width 0.5s ease",
  }),
  total: {
    fontFamily: fonts.mono,
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.01em",
  },
  empty: {
    padding: "20px 0",
    textAlign: "center",
    color: colors.textSoft,
    fontSize: 13,
    fontWeight: 500,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal de pedidos por UF
// ─────────────────────────────────────────────────────────────────────────────
export const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 24,
  },
  box: {
    background: colors.surface,
    borderRadius: 18,
    border: `1px solid ${colors.border}`,
    width: "90%",
    maxWidth: "none",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 20px 16px",
    borderBottom: `1px solid ${colors.border}`,
    gap: 16,
  },
  headLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: colors.text,
    margin: 0,
    textTransform: "uppercase",
  },
  subtitle: {
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: 500,
    letterSpacing: "0.04em",
  },
  headActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  exportBtn: {
    cursor: "pointer",
    border: `1px solid ${colors.good}`,
    background: colors.goodSoft,
    color: colors.good,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "0.03em",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "background 0.15s ease",
  },
  close: {
    cursor: "pointer",
    border: "none",
    background: colors.bg,
    color: colors.textMuted,
    width: 32,
    height: 32,
    borderRadius: "50%",
    fontSize: 18,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    transition: "background 0.15s ease, color 0.15s ease",
  },
  body: {
    overflowY: "auto",
    overflowX: "auto",
    padding: "0 20px 20px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    fontVariantNumeric: "tabular-nums",
  },
  th: {
    textAlign: "left",
    fontWeight: 700,
    color: colors.textMuted,
    fontSize: 11,
    letterSpacing: "0.08em",
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bg,
    position: "sticky",
    top: 0,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 16px",
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  tdMono: {
    fontFamily: fonts.mono,
    fontSize: 12,
  },
  badge: (variant) => {
    const map = {
      success: { bg: colors.goodSoft, color: colors.good },
      late: { bg: colors.badSoft, color: colors.bad },
      transit: { bg: "#E2EDF7", color: colors.stages[2] },
      warning: { bg: colors.stagesSoft[1], color: colors.stages[1] },
      neutral: { bg: colors.neutralSoft, color: colors.neutral },
    };
    const c = map[variant] || map.neutral;
    return {
      display: "inline-block",
      padding: "3px 8px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.02em",
      color: c.color,
      background: c.bg,
      whiteSpace: "nowrap",
    };
  },
  empty: {
    padding: "30px 16px",
    textAlign: "center",
    color: colors.textSoft,
    fontSize: 13,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function compareState(value, baseline) {
  if (value == null || baseline == null) return null;
  if (value < baseline) return "below";
  if (value > baseline) return "above";
  return "equal";
}
